import { FormEvent, useEffect, useRef, useState } from 'react'
import { ArrowRight, ArrowUp, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ViralityMeter } from '@/components/virality-meter'
import { pulse } from '@/lib/pulse'
import { startGuestSession } from '@/lib/guest'
import { cn } from '@/lib/utils'

/**
 * The front page's quick chat: an idea in, three scored takes out, pick one.
 *
 * Deliberately small. One exchange on screen at a time, three ideas to tap
 * instead of a blank box, one status line while it works. The full chat is
 * one click away: "Keep going" opens the public demo with the picked take.
 * The server enforces the per-IP quota; this only shows it.
 */

interface DemoDraft {
  text: string
  score: number
  angle?: string | null
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'

const IDEAS = ['why I stopped cold emailing', 'AI agents doing real work', 'my first paying customer']
const STAGES = ['Reading your idea', 'Writing eight takes', 'Scoring them']
const STAGE_DELAYS_MS = [0, 1800, 7000]

export function LandingDemoChat({ onSignIn, className }: { onSignIn: () => void; className?: string }) {
  const [input, setInput] = useState('')
  const [idea, setIdea] = useState<string | null>(null)
  const [drafts, setDrafts] = useState<DemoDraft[]>([])
  const [selected, setSelected] = useState(0)
  const [loading, setLoading] = useState(false)
  const [stage, setStage] = useState(0)
  const [note, setNote] = useState<string | null>(null)
  const [remaining, setRemaining] = useState<number | null>(null)
  const [exhausted, setExhausted] = useState(false)
  const [opening, setOpening] = useState(false)
  const timers = useRef<number[]>([])

  useEffect(() => () => timers.current.forEach(clearTimeout), [])

  const send = async (raw: string) => {
    const text = raw.trim()
    if (loading || exhausted || text.length < 8) return
    setIdea(text)
    setInput('')
    setDrafts([])
    setNote(null)
    setSelected(0)
    setLoading(true)
    setStage(0)
    pulse.trackEvent('demo_chat_message_sent', { length: text.length })
    timers.current.forEach(clearTimeout)
    timers.current = STAGE_DELAYS_MS.map((delay, index) => window.setTimeout(() => setStage(index), delay))
    try {
      const response = await fetch(`${API_BASE}/api/demo/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idea: text }),
      })
      const payload = await response.json()
      if (!response.ok) {
        if (payload?.quotaExceeded) {
          setExhausted(true)
          setRemaining(0)
        }
        setNote(payload?.error || 'That one did not go through. Try again.')
        return
      }
      if (typeof payload.remaining === 'number') {
        setRemaining(payload.remaining)
        if (payload.remaining <= 0) setExhausted(true)
      }
      setDrafts((payload.drafts ?? []).slice(0, 3))
      pulse.trackEvent('demo_chat_drafts_shown', { count: payload.drafts?.length ?? 0 })
    } catch {
      setNote('That one did not go through. Try again.')
    } finally {
      timers.current.forEach(clearTimeout)
      setLoading(false)
    }
  }

  const keepGoing = async () => {
    setOpening(true)
    pulse.trackEvent('demo_chat_keep_going', { hasDraft: drafts.length > 0 })
    const draft = drafts[selected]?.text
    try {
      await startGuestSession(draft ? `/chat?compose=${encodeURIComponent(draft)}` : '/chat')
    } catch (error: any) {
      setOpening(false)
      setNote(error?.message ?? 'The demo is busy. Try again in a minute.')
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void send(input)
  }

  return (
    <div className={cn('float-slab w-full overflow-hidden rounded-2xl border border-border bg-card text-left', className)}>
      <div className="flex items-center justify-between gap-3 px-5 pb-3 pt-4">
        <p className="text-sm font-semibold">Try it: one idea, three takes</p>
        <span className="key key-lite shrink-0 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] text-muted-foreground">
          {remaining === null ? 'No account needed' : `${remaining} left today`}
        </span>
      </div>

      <form
        onSubmit={submit}
        className="field-well mx-4 flex items-center gap-2 rounded-xl py-1.5 pl-3.5 pr-1.5"
      >
        <input
          value={input}
          onChange={event => setInput(event.target.value)}
          placeholder={exhausted ? 'Done for today. Keep going in the demo.' : 'Type an idea. Rough is fine.'}
          maxLength={400}
          disabled={loading || exhausted}
          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          aria-label="Your idea"
        />
        <Button type="submit" size="icon" className="h-9 w-9 shrink-0" disabled={loading || exhausted || input.trim().length < 8} aria-label="Write takes">
          <ArrowUp className="h-4 w-4" />
        </Button>
      </form>

      <div className="px-5 pb-5 pt-3.5">
        {!idea && (
          <div className="flex flex-wrap gap-1.5">
            {IDEAS.map(option => (
              <button key={option} type="button" onClick={() => void send(option)} className="key key-lite rounded-full px-3 py-1 text-xs">
                {option}
              </button>
            ))}
          </div>
        )}

        {idea && (
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span className="min-w-0 truncate">On "{idea}"</span>
            {!loading && (
              <button type="button" onClick={() => { setIdea(null); setDrafts([]); setNote(null) }} className="inline-flex shrink-0 items-center gap-1 hover:text-foreground">
                <RotateCcw className="h-3 w-3" /> New idea
              </button>
            )}
          </div>
        )}

        {loading && (
          <p className="mt-3 text-sm" aria-live="polite">
            <span key={stage} className="thinking-shimmer thinking-reveal">{STAGES[stage]}</span>
          </p>
        )}

        {drafts.length > 0 && (
          <ol className="mt-3 space-y-2.5">
            {drafts.map((draft, index) => (
              <li key={index}>
                <button
                  type="button"
                  onClick={() => setSelected(index)}
                  aria-pressed={selected === index}
                  className={cn(
                    'slab thinking-reveal w-full rounded-xl border bg-card px-3.5 py-3 text-left transition-[border-color,box-shadow]',
                    selected === index ? 'border-foreground ring-1 ring-foreground' : 'border-border hover:border-foreground/30',
                  )}
                  style={{ animationDelay: `${index * 90}ms` }}
                >
                  <p className={cn('text-sm leading-6', selected !== index && 'line-clamp-2')}>{draft.text}</p>
                  <span className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
                    <ViralityMeter score={draft.score} />
                    {draft.angle && <span>{draft.angle}</span>}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        )}

        {note && <p className="mt-3 text-xs text-muted-foreground">{note}</p>}

        {(drafts.length > 0 || exhausted) && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" className="flex-1" onClick={() => void keepGoing()} disabled={opening}>
              {opening ? 'Opening the demo' : 'Keep going in the demo'}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
            {drafts.length > 0 && (
              <Button size="sm" variant="outline" onClick={onSignIn}>
                Post it with X
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
